import { useReducedMotion } from 'framer-motion'
import { BarChart3 } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { CaptacaoTabView } from '@/components/Dashboard/captacaoTab.utils'
import { formatDashboardNumber } from '@/utils/crmValuesDisplay'

const STATUS_COLORS: Record<string, string> = {
  Novo: '#3B82F6',
  'Em contato': '#6366F1',
  Qualificado: '#10B981',
  Nutrição: '#84CC16',
  Convertido: '#14B8A6',
  Aguardando: '#F59E0B',
  Perdido: '#EF4444',
  Descartado: '#F97316',
  Cancelado: '#FB7185',
  Abandonado: '#6B7280',
}

type Props = {
  view: CaptacaoTabView
  valuesVisible?: boolean
}

export function CaptacaoStatusChart({ view, valuesVisible = false }: Props) {
  const reduced = useReducedMotion()
  const chartData = view.leadsByStatus.map((row) => ({
    status: row.label,
    count: row.count,
    fill: STATUS_COLORS[row.label] ?? 'var(--brand-primary)',
  }))

  return (
    <div className='flex h-full flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm'>
      <div className='mb-4'>
        <div className='flex items-center gap-2'>
          <BarChart3 size={16} className='text-accent' aria-hidden />
          <h3 className='text-base font-semibold text-text-primary'>Leads por status</h3>
        </div>
        <p className='mt-1 text-sm text-text-muted'>Distribuição das entradas na carteira closer</p>
      </div>

      {chartData.length > 0 ? (
        <>
          <ResponsiveContainer width='100%' height={240}>
            <BarChart data={chartData} layout='vertical' margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' horizontal={false} />
              <XAxis
                type='number'
                allowDecimals={false}
                tick={{ fill: 'var(--font-color-muted)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type='category'
                dataKey='status'
                width={88}
                tick={{ fill: 'var(--font-color-muted)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                }}
                formatter={(value) => [
                  formatDashboardNumber(Number(value ?? 0), valuesVisible),
                  'Leads',
                ]}
              />
              <Bar
                dataKey='count'
                radius={[0, 6, 6, 0]}
                isAnimationActive={!reduced}
              />
            </BarChart>
          </ResponsiveContainer>

          <ul className='mt-3 space-y-2 border-t border-[var(--border)] pt-3'>
            {chartData.map((row) => (
              <li key={row.status} className='flex items-center justify-between text-sm'>
                <span className='flex items-center gap-2 text-text-secondary'>
                  <span
                    className='h-2.5 w-2.5 rounded-full'
                    style={{ backgroundColor: row.fill }}
                    aria-hidden
                  />
                  {row.status}
                </span>
                <span className='font-semibold tabular-nums text-text-primary'>
                  {formatDashboardNumber(row.count, valuesVisible)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className='flex flex-1 flex-col items-center justify-center gap-2 py-12 text-center'>
          <p className='text-sm font-medium text-text-primary'>Nenhum lead captado no período</p>
          <p className='max-w-sm text-sm text-text-muted'>
            {view.activeLeads > 0
              ? 'Há leads ativos na carteira, mas nenhuma entrada nova neste intervalo.'
              : 'Ajuste o período ou cadastre leads na carteira closer.'}
          </p>
        </div>
      )}
    </div>
  )
}
